import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import api from 'api';
import { PdfViewer } from 'components/PdfViewer';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import LinkToIcon from 'icons/LinkTo.icon';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './PreviewWatermark.styles.scss';

type PreviewWatermarkProps = {
  className?: string;
  disabled?: boolean;
  params: {
    watermarkDiagonal: boolean;
    watermarkHeader: boolean;
    watermarkFooter: boolean;
    watermarkFullName: boolean;
    watermarkEmail: boolean;
    watermarkIpAddress: boolean;
  };
};

const PreviewWatermark: React.FC<PreviewWatermarkProps> = (props) => {
  const { className, disabled, params } = props;

  const {
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
  } = params;

  const classes = classNames('preview-watermark', className);

  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const hasPosition = watermarkDiagonal || watermarkHeader || watermarkFooter;
  const hasContent = watermarkFullName || watermarkEmail || watermarkIpAddress;

  const isDisabled = disabled || isLoading || !hasPosition || !hasContent;

  const { t } = useTranslation();
  const getMixpanelProps = useGetMixpanelProps();

  const onPreview = useCallback(async () => {
    try {
      setIsLoading(true);

      const {
        data: { url },
      } = await api.general.getPreviewForWatermark(params);

      if (url) {
        setPreviewUrl(url);
        modal.open();
      }
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
    } finally {
      setIsLoading(false);
    }
  }, [getMixpanelProps, modal, params]);

  return (
    <div className={classes}>
      <Button
        variant="link"
        disabled={isDisabled}
        icon={LinkToIcon}
        onClick={onPreview}
      >
        {t('previewWatermark')}
      </Button>

      <Modal ref={modalRef} className="preview-file-modal-template">
        <PdfViewer url={previewUrl} canZoom={false} />
      </Modal>
    </div>
  );
};

export default PreviewWatermark;
